import {
  useDisclosure,
  Spinner,
  Flex,
  HStack,
  Text,
  Badge,
  Checkbox,
} from '@chakra-ui/react';
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  useGetPipelineRunsQuery,
  useGetTapeBuilderLatestPipelineRunQuery,
} from '../../requests/tapeBuilder.gql.generated';
import { CalendarModal } from './CalendarModal';
import { DateAndVersionInput } from './DateAndVersionInput';
import { getMonthDateRange } from './datePickerUtils';
import { SelectedPipeline } from './SelectedPipeline';

// displays selected pipeline, and allows user to pick existing pipeline.
// provides a shortcut to quickly select latest pipeline
// monitors for any latest pipeline changes
export const TapeBuilderPipelinePicker = ({
  pipelineDefinitionId,
  loadArtifactInstances,
}: {
  pipelineDefinitionId: string;
  loadArtifactInstances: (pipelineRunId: string) => void;
}) => {
  const [pollForUpdates, setPollForUpdates] = useState<boolean>(false);
  const [
    selectedPipeline,
    setSelectedPipeline,
  ] = useState<SelectedPipeline | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [searchParams, setSearchParams] = useSearchParams();

  // A deep link may pin a specific run via `?reportingDate=YYYY-MM-DD&fileVersion=N`.
  // We resolve those to a concrete pipelineRunId below and seed the selection.
  const urlReportingDate = searchParams.get(
    'reportingDate',
  ) as Util.FullDateStr | null;
  const urlFileVersionRaw = searchParams.get('fileVersion');
  const urlFileVersion =
    urlFileVersionRaw !== null ? Number(urlFileVersionRaw) : null;
  const hasUrlRun =
    urlReportingDate !== null &&
    urlFileVersion !== null &&
    !Number.isNaN(urlFileVersion);

  // Fetch the runs for the deep-linked date's month so we can map
  // (reportingDate, fileVersion) -> pipelineRunId. Skipped when no deep link.
  const monthRange = hasUrlRun
    ? getMonthDateRange(commonDate.fullDateStrToDate(urlReportingDate))
    : null;
  const { data: urlRunData } = useGetPipelineRunsQuery({
    fetchPolicy: 'network-only',
    skip: !hasUrlRun || monthRange === null,
    variables: {
      pipelineDefinitionId,
      startDate: monthRange?.startDate ?? ('' as Util.FullDateStr),
      endDate: monthRange?.endDate ?? ('' as Util.FullDateStr),
    },
  });

  const {
    data: latestData,
    loading: latestLoading,
    error: latestError,
  } = useGetTapeBuilderLatestPipelineRunQuery({
    variables: {
      pipelineDefinitionId,
    },
    fetchPolicy: 'network-only',
    pollInterval: pollForUpdates ? 2000 : 0,
  });

  // when selectedPipeline changes, load artifact instances
  const selectedRunId = selectedPipeline?.pipelineRunId;
  useEffect(() => {
    if (selectedRunId) {
      loadArtifactInstances(selectedRunId);
    }
  }, [selectedRunId, loadArtifactInstances]);

  // when polling for updates load artifact instances
  // of selected pipeline at regular intervals
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (pollForUpdates && selectedRunId) {
        loadArtifactInstances(selectedRunId);
      }
    }, 2000);
    return () => clearInterval(intervalId);
  }, [pollForUpdates, selectedRunId, loadArtifactInstances]);

  const latestRun = latestData?.getPipelineRuns?.[0];

  // Seed the initial selection exactly once: prefer a deep-linked run, then
  // fall back to the latest run. `hasSeeded` prevents this from fighting later
  // user selections (and from re-selecting a run the user has cleared).
  const hasSeeded = useRef(false);
  useEffect(() => {
    if (hasSeeded.current || selectedPipeline) {
      return;
    }
    if (hasUrlRun) {
      // Wait for the month's runs before deciding.
      if (!urlRunData?.getPipelineRuns) {
        return;
      }
      const match = urlRunData.getPipelineRuns.find(
        (run) =>
          run.reportingDate === urlReportingDate &&
          run.fileVersion === urlFileVersion,
      );
      hasSeeded.current = true;
      // Fall back to latest if the deep-linked run no longer exists.
      setSelectedPipeline(match ?? latestRun ?? null);
      return;
    }
    if (latestRun) {
      hasSeeded.current = true;
      setSelectedPipeline(latestRun);
    }
  }, [
    hasUrlRun,
    urlRunData,
    urlReportingDate,
    urlFileVersion,
    latestRun,
    selectedPipeline,
  ]);

  // Keep the URL in sync with the current selection so the address bar is
  // always a shareable deep link into this monitor-mode run.
  useEffect(() => {
    if (!selectedPipeline) {
      return;
    }
    const curDate = searchParams.get('reportingDate');
    const curVersion = searchParams.get('fileVersion');
    if (
      curDate === selectedPipeline.reportingDate &&
      curVersion === String(selectedPipeline.fileVersion)
    ) {
      return;
    }
    setSearchParams(
      (params) => {
        params.set('reportingDate', selectedPipeline.reportingDate);
        params.set('fileVersion', String(selectedPipeline.fileVersion));
        return params;
      },
      { replace: true },
    );
  }, [selectedPipeline, searchParams, setSearchParams]);

  if (!latestData) {
    if (latestLoading) {
      return (
        <Flex justifyContent="center" alignItems="center" height="100%">
          <Spinner />
          <Text ml={2}>Loading latest pipeline...</Text>
        </Flex>
      );
    }

    if (latestError) {
      return (
        <Flex justifyContent="center" alignItems="center" height="100%">
          <Text color="red.500">Error loading latest pipeline</Text>
        </Flex>
      );
    }

    return (
      <Flex justifyContent="center" alignItems="center" height="100%">
        <Text color="red.500">Latest data not available</Text>
      </Flex>
    );
  }

  // if selected pipeline is not latest pipeline - show a badge that lets you select latest pipeline
  let latestBadgePipeline: SelectedPipeline | null = null;
  if (
    latestData.getPipelineRuns &&
    (!selectedPipeline ||
      latestData.getPipelineRuns[0].pipelineRunId !==
        selectedPipeline.pipelineRunId)
  ) {
    latestBadgePipeline = latestData.getPipelineRuns[0];
  }

  return (
    <>
      {/* The run-selection controls (which run to view + live polling). The
          two-tier status bar's context row is the surrounding visual group. */}
      <HStack spacing={3} alignItems="center">
        <Text fontSize="xs" color="gray.500" fontWeight="medium">
          Run
        </Text>
        <DateAndVersionInput
          selectedPipeline={selectedPipeline}
          onClick={onOpen}
        >
          {latestBadgePipeline && (
            <Badge
              ml={2}
              colorScheme="yellow"
              variant="outline"
              onClick={(event) => {
                // you can navigate to current pipeline by clicking on badge
                event.stopPropagation();
                setSelectedPipeline(latestBadgePipeline);
              }}
            >
              Goto latest
            </Badge>
          )}
        </DateAndVersionInput>
        <Checkbox
          isChecked={pollForUpdates}
          onChange={(e) => setPollForUpdates(e.target.checked)}
        >
          Auto-poll
        </Checkbox>
      </HStack>
      {isOpen && (
        <CalendarModal
          selectedPipeline={selectedPipeline}
          setSelectedPipeline={setSelectedPipeline}
          onClose={onClose}
          pipelineDefinitionId={pipelineDefinitionId}
        />
      )}
    </>
  );
};
